// src/pages/InvoicesPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "../DataContext";
import {
  ApiInvoice,
  apiInvoiceToUi,
  createInvoice,
  deleteInvoiceById,
  listInvoices,
  updateInvoiceById,
  uiInvoiceToApi,
} from "../api/invoices";
import { useTranslation } from "react-i18next";

const money = (n: number) =>
  `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

type LineItem = { amount?: number; taxable?: boolean };
type Adjustment = { amount?: number };

function safeJson<T>(s: string, fallback: T): T {
  try {
    const v = JSON.parse(s);
    return (v ?? fallback) as T;
  } catch {
    return fallback;
  }
}

function computeInvoiceTotal(row: ApiInvoice) {
  const items = safeJson<LineItem[]>(row.items_json || "[]", []);
  const adjustments = safeJson<Adjustment[]>(row.adjustments_json || "[]", []);

  const subtotal = items.reduce((sum, it) => sum + (Number(it.amount) || 0), 0);
  const adjTotal = adjustments.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);

  const taxableSubtotal = items.reduce((sum, it) => {
    const amt = Number(it.amount) || 0;
    return it.taxable ? sum + amt : sum;
  }, 0);

  const taxEnabled = Number(row.tax_enabled) ? 1 : 0;
  const taxRate = Number(row.tax_rate) || 0;
  const tax = taxEnabled ? taxableSubtotal * taxRate : 0;

  const total = subtotal + adjTotal + tax;
  return { subtotal, adjTotal, taxableSubtotal, tax, total };
}

export default function InvoicesPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const data = useData() as any;

  const tenantId: string = (data?.activeTenantId || data?.tenantId || "demo").toString();

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [rows, setRows] = useState<ApiInvoice[]>([]);
  const [search, setSearch] = useState("");

  // per-row busy state (so inline actions don't lock the whole page)
  const [rowBusyId, setRowBusyId] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setErr(null);
    try {
      const res = await listInvoices(tenantId);
      if (!res.ok) {
        setErr(res.error || "Failed to load invoices");
        setRows([]);
      } else {
        setRows(res.data || []);
      }
    } catch (e: any) {
      setErr(e?.message || String(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const uiRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    const mapped = (rows || []).map((r) => {
      const ui = apiInvoiceToUi(r);
      const totals = computeInvoiceTotal(r);
      return { raw: r, ui, totals };
    });

    if (!q) return mapped;

    return mapped.filter(({ raw, ui }) => {
      const hay =
        `${ui.invoiceNumber} ${ui.projectLabel} ${ui.billToName} ${ui.billToAddress} ${ui.invoiceDate} ${ui.status} ${ui.invoiceType} ${raw.id}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search]);

  async function onCreate() {
    setLoading(true);
    setErr(null);
    try {
      const draftNumber = `INV-${new Date().toISOString().slice(0, 10)}-${String(Date.now()).slice(-6)}`;

      const draftUi = {
        invoiceNumber: draftNumber,
        invoiceType: "detailed" as const,
        title: "Invoice",
        invoiceDate: new Date().toISOString().slice(0, 10),
        dueDate: null,

        projectId: null,
        projectLabel: "Unassigned Project",

        billToName: "TBD",
        billToAddress: "TBD",

        scopeText: "",
        notesText: "",

        taxEnabled: false,
        taxRate: 0,

        status: "draft" as const,

        itemsJson: "[]",
        adjustmentsJson: "[]",
        appliedPaymentIdsJson: "[]",
      };

      const body = uiInvoiceToApi(draftUi);
      const created = await createInvoice(tenantId, body);

      if (!created.ok) {
        setErr(created.error || "Failed to create invoice");
        return;
      }

      const id = (created.data as any)?.id;
      await refresh();
      if (id) nav(`/invoices/${id}`);
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }

  async function setInvoiceType(id: string, nextType: "detailed" | "change_order") {
    setRowBusyId(id);
    setErr(null);
    try {
      const res = await updateInvoiceById(tenantId, id, { invoice_type: nextType });
      if (!res.ok) {
        setErr(res.error || "Failed to update invoice type");
        return;
      }
      await refresh();
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setRowBusyId(null);
    }
  }

  async function toggleInvoiceStatus(id: string, current: "draft" | "final") {
    const next = current === "final" ? "draft" : "final";
    const ok = window.confirm(
      next === "final" ? t("invoices.finalizeConfirm") : t("invoices.revertDraftConfirm")
    );
    if (!ok) return;

    setRowBusyId(id);
    setErr(null);
    try {
      const res = await updateInvoiceById(tenantId, id, { status: next });
      if (!res.ok) {
        setErr(res.error || "Failed to update invoice status");
        return;
      }
      await refresh();
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setRowBusyId(null);
    }
  }

  async function deleteInvoice(id: string) {
    const ok = window.confirm(t("invoices.deleteConfirm"));
    if (!ok) return;

    setRowBusyId(id);
    setErr(null);
    try {
      const res = await deleteInvoiceById(tenantId, id);
      if (!res.ok) {
        setErr(res.error || "Delete failed");
        return;
      }
      await refresh();
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setRowBusyId(null);
    }
  }

  return (
    <div className="container">
      {/* Header pattern (matches ClientsPage) */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "end",
          flexWrap: "wrap",
        }}
      >
        <div>
          <h1 style={{ marginBottom: 6 }}>Invoices</h1>
          <div className="pageNote">{t("invoices.pageNote")}</div>
        </div>

        {/* Top-right search (standard) */}
        <div style={{ minWidth: 280, flex: 1, maxWidth: 520 }}>
          <label
            style={{
              display: "block",
              fontSize: 12,
              color: "var(--pageMuted)",
            }}
          >
            {t("common.search")}
          </label>
          <input
            className="input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("invoices.searchPlaceholder")}
            style={{ width: "100%" }}
          />
        </div>
      </div>

      {err ? (
        <div className="card cardTight" style={{ marginTop: 14, marginBottom: 14 }}>
          <div className="formError" role="alert">
            {err}
          </div>
        </div>
      ) : null}

      {/* CREATE INVOICE */}
      <div className="card cardTight" style={{ marginTop: 14, marginBottom: 14 }}>
        <div className="cardHeader">
          <h3 className="cardTitle">{t("invoices.createTitle")}</h3>
          <div className="cardSub">{loading ? "Working..." : "Drafts start editable"}</div>
        </div>

        <div className="formActions">
          <button className="btn" onClick={onCreate} type="button" disabled={loading}>{t("invoices.newInvoiceBtn")}</button>
          <button className="btn btnSecondary" onClick={refresh} type="button" disabled={loading}>{t("common.refresh")}</button>
        </div>
      </div>

      {/* INVOICES TABLE */}
      <div className="card">
        <div className="cardHeader">
          <h3 className="cardTitle">{t("invoices.listTitle")}</h3>
          <div className="cardSub">{t("invoices.invoiceCount", { count: uiRows.length })}</div>
        </div>

        <div className="tableWrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 190 }}>{t("invoices.colInvoiceNum")}</th>
                <th style={{ width: 170 }}>{t("common.type")}</th>
                <th>{t("common.project")}</th>
                <th style={{ width: 130 }}>{t("common.date")}</th>
                <th style={{ width: 150, textAlign: "right" }}>{t("common.total")}</th>
                <th style={{ width: 200 }}>{t("common.status")}</th>
                <th style={{ width: 140, textAlign: "right" }}>{t("common.actions")}</th>

              </tr>
            </thead>

            <tbody>
              {uiRows.map(({ raw, ui, totals }) => {
                const busy = rowBusyId === raw.id;
                const isFinal = ui.status === "final";

                return (
                  <tr
                    key={raw.id}
                    onClick={() => nav(`/invoices/${raw.id}`)}
                    title={t("invoices.openInvoice")}
                    style={{ cursor: "pointer" }}
                  >
                    <td style={{ fontWeight: 800 }}>
                      <div className="cellClamp" title={ui.invoiceNumber || ""}>
                        {ui.invoiceNumber}
                      </div>
                    </td>

                    <td onClick={(e) => e.stopPropagation()} style={{ cursor: "default" }}>
                      <select
                        className="input"
                        value={ui.invoiceType}
                        disabled={busy || isFinal}
                        onChange={(e) =>
                          setInvoiceType(
                            raw.id,
                            (e.target.value === "change_order" ? "change_order" : "detailed") as any
                          )
                        }
                        title={isFinal ? "Final invoices are locked" : "Change invoice type"}
                      >
                        <option value="detailed">{t("invoices.typeDetailed")}</option>
                        <option value="change_order">{t("invoices.typeChangeOrder")}</option>
                      </select>
                    </td>

                    <td style={{ color: "var(--muted)" }}>
                      <div className="cellClamp" title={ui.projectLabel || ""}>
                        {ui.projectLabel || "—"}
                      </div>
                    </td>

                    <td>{ui.invoiceDate}</td>

                    <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {money(totals.total)}
                    </td>

                    <td onClick={(e) => e.stopPropagation()} style={{ cursor: "default" }}>
                      <div className="segToggle" role="group" aria-label={t("invoices.statusLabel")}>
                        <button
                          type="button"
                          className={`segToggleBtn ${!isFinal ? "active" : ""}`}
                          disabled={busy}
                          onClick={() => {
                            if (isFinal) toggleInvoiceStatus(raw.id, "final");
                          }}
                          title={!isFinal ? "Draft" : "Set to Draft"}
                        >
                          Draft
                        </button>
                        <button
                          type="button"
                          className={`segToggleBtn ${isFinal ? "active" : ""}`}
                          disabled={busy}
                          onClick={() => {
                            if (!isFinal) toggleInvoiceStatus(raw.id, "draft");
                          }}
                          title={isFinal ? "Final" : "Set to Final"}
                        >
                          Final
                        </button>
                      </div>
                    </td>

                    <td style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                      <div className="actionIcons" style={{ justifyContent: "flex-end" }}>
                        <button
                          className="iconActionBtn primary"
                          type="button"
                          title={t("invoices.openInvoice")}
                          aria-label={t("invoices.openInvoice")}
                          onClick={() => nav(`/invoices/${raw.id}`)}
                          disabled={busy}
                        >
                          <span className="iconGlyph">✎</span>
                        </button>

                        <button
                          className="iconActionBtn danger"
                          type="button"
                          disabled={busy || isFinal}
                          title={isFinal ? "Only Draft invoices can be deleted" : "Delete invoice"}
                          aria-label={t("invoices.deleteInvoice")}
                          onClick={() => deleteInvoice(raw.id)}
                        >
                          <span className="iconGlyph">🗑️</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {!loading && uiRows.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ color: "var(--muted)", fontStyle: "italic", padding: 14 }}>
                    {t("invoices.empty")}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="pageNote" style={{ marginTop: 10 }}>
          {t("invoices.tip")}
        </div>
      </div>
    </div>
  );
}

